/**
 * StorageContentsTab — contents management for STORAGE_UNIT assets.
 * Supports tags for searchable categorization (e.g. "mawarputih", "rawfootage").
 */

import { useState, useRef, useEffect } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  HardDrive,
  FolderOpen,
  X,
  Tag,
} from "lucide-react";
import {
  useStorageContents,
  useCreateStorageContent,
  useUpdateStorageContent,
  useDeleteStorageContent,
  useContentSuggestions,
} from "../../hooks/useAssets";
import { Button, Input, Spinner } from "../ui/index.jsx";
import apiClient from "../../api/client";

function slugifyTag(raw) {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

// ---------------------------------------------------------------------------
// Tag input with autocomplete
// ---------------------------------------------------------------------------
function TagInput({ tags, onChange }) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef();
  const debounceRef = useRef();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!input.trim()) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      apiClient
        .get("/api/storage/contents/tags", {
          params: { q: input.toLowerCase() },
        })
        .then((r) => setSuggestions(r.data.filter((t) => !tags.includes(t))))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [input, tags]);

  function addTag(raw) {
    const tag = slugifyTag(raw);
    if (!tag || tags.includes(tag) || tags.length >= 20) return;
    onChange([...tags, tag]);
    setInput("");
    setSuggestions([]);
    inputRef.current?.focus();
  }

  function removeTag(t) {
    onChange(tags.filter((x) => x !== t));
  }

  function handleKey(e) {
    if (["Enter", ",", " "].includes(e.key)) {
      e.preventDefault();
      if (input.trim()) addTag(input);
    } else if (e.key === "Backspace" && !input && tags.length) {
      removeTag(tags[tags.length - 1]);
    }
  }

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 block mb-1">
        Tags
        <span className="text-xs font-normal text-slate-400 ml-2">
          press Enter or comma to add · max 20
        </span>
      </label>

      {/* Tag pills + input */}
      <div
        className="flex flex-wrap gap-1.5 min-h-10 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 text-xs font-medium bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full"
          >
            #{t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              className="hover:text-primary-900 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder={
            tags.length === 0 ? "e.g. mawarputih, rawfootage, 2025..." : ""
          }
          className="flex-1 min-w-24 text-sm outline-none bg-transparent py-0.5"
        />
      </div>

      {/* Autocomplete dropdown */}
      {suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          {suggestions.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => addTag(t)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
            >
              <Tag className="h-3 w-3 text-slate-400" />#{t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Content form
// ---------------------------------------------------------------------------
function ContentForm({ initial = {}, onSave, onCancel, isLoading }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    projectName: initial.projectName || "",
    sizeGB: initial.sizeGB || "",
    tags: initial.tags || [],
    notes: initial.notes || "",
  });
  const [error, setError] = useState("");

  function set(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    onSave({
      name: form.name.trim(),
      projectName: form.projectName.trim() || null,
      sizeGB: form.sizeGB ? parseFloat(form.sizeGB) : null,
      tags: form.tags,
      notes: form.notes.trim() || null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Input
        label="Content name *"
        placeholder="e.g. Mawar Putih Raw Footage, Archive 2025"
        value={form.name}
        onChange={(e) => {
          set("name", e.target.value);
          setError("");
        }}
        error={error}
      />
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Project"
          placeholder="e.g. Mawar Putih"
          value={form.projectName}
          onChange={(e) => set("projectName", e.target.value)}
        />
        <Input
          label="Size (GB)"
          type="number"
          min="0"
          step="0.01"
          placeholder="e.g. 256"
          value={form.sizeGB}
          onChange={(e) => set("sizeGB", e.target.value)}
        />
      </div>

      {/* Tags */}
      <div className="relative">
        <TagInput tags={form.tags} onChange={(tags) => set("tags", tags)} />
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 block mb-1">
          Notes
        </label>
        <textarea
          rows={2}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          placeholder="Additional notes..."
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          Save
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main component
//
// Capacity/usage used to be estimated here from the sum of manually-typed
// sizeGB fields on curated Contents entries — replaced by a real number
// computed from the actual File Index scan (see DriveCapacitySummary in
// AssetDetail.jsx), since that's exhaustive and machine-measured rather
// than dependent on someone remembering to fill in a size.
// ---------------------------------------------------------------------------
export function StorageContentsTab({ assetId, canEdit }) {
  const { data: contents = [], isLoading } = useStorageContents(assetId);
  const { data: suggestions = [] } = useContentSuggestions(assetId);
  const createMutation = useCreateStorageContent(assetId);
  const updateMutation = useUpdateStorageContent(assetId);
  const deleteMutation = useDeleteStorageContent(assetId);

  const [showAdd, setShowAdd] = useState(false);
  const [addInitial, setAddInitial] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filterTag, setFilterTag] = useState("");

  function openAddFromSuggestion(name) {
    setAddInitial({ name, tags: [slugifyTag(name)].filter(Boolean) });
    setShowAdd(true);
  }

  async function handleCreate(data) {
    await createMutation.mutateAsync(data);
    setShowAdd(false);
    setAddInitial(null);
  }

  async function handleUpdate(contentId, data) {
    await updateMutation.mutateAsync({ contentId, ...data });
    setEditingId(null);
  }

  async function handleDelete(contentId) {
    if (!confirm("Remove this content entry?")) return;
    setDeletingId(contentId);
    try {
      await deleteMutation.mutateAsync(contentId);
    } finally {
      setDeletingId(null);
    }
  }

  // Collect all unique tags from this storage unit's contents
  const allTags = [...new Set(contents.flatMap((c) => c.tags || []))].sort();

  // Filter by selected tag
  const filtered = filterTag
    ? contents.filter((c) => c.tags?.includes(filterTag))
    : contents;

  if (isLoading)
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-slate-700">
          Contents
          {contents.length > 0 && (
            <span className="ml-2 text-xs font-normal text-slate-400">
              {contents.length} {contents.length === 1 ? "entry" : "entries"} ·{" "}
              {contents
                .reduce((s, c) => s + (parseFloat(c.sizeGB) || 0), 0)
                .toFixed(1)}{" "}
              GB tracked
            </span>
          )}
        </p>
        {canEdit && !showAdd && (
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        )}
      </div>

      {/* Suggestions from the File Index's top-level folders — not yet catalogued */}
      {canEdit && suggestions.length > 0 && (
        <div className="mb-4 rounded-lg bg-primary-50 border border-primary-100 p-3">
          <p className="text-xs font-medium text-primary-700 mb-2">
            Found in File Index, not yet added as content:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => openAddFromSuggestion(name)}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white border border-primary-200 text-primary-700 hover:bg-primary-100 transition-colors"
              >
                <Plus className="h-3 w-3" /> {name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tag filter pills */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          <button
            type="button"
            onClick={() => setFilterTag("")}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              !filterTag
                ? "bg-primary-600 text-white border-primary-600"
                : "border-slate-300 text-slate-500 hover:border-primary-300"
            }`}
          >
            All
          </button>
          {allTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setFilterTag(filterTag === t ? "" : t)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                filterTag === t
                  ? "bg-primary-600 text-white border-primary-600"
                  : "border-slate-300 text-slate-500 hover:border-primary-300"
              }`}
            >
              #{t}
            </button>
          ))}
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 mb-4">
          <p className="text-sm font-semibold text-slate-700 mb-3">
            New content entry
          </p>
          <ContentForm
            initial={addInitial || {}}
            onSave={handleCreate}
            onCancel={() => {
              setShowAdd(false);
              setAddInitial(null);
            }}
            isLoading={createMutation.isPending}
          />
        </div>
      )}

      {/* List */}
      {filtered.length === 0 && !showAdd ? (
        <div className="text-center py-10 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <FolderOpen className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">
            {filterTag
              ? `No entries tagged #${filterTag}`
              : "No content entries yet."}
          </p>
          {canEdit && !filterTag && (
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              onClick={() => setShowAdd(true)}
            >
              <Plus className="h-4 w-4" /> Add first entry
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((content) => (
            <div key={content.id}>
              {editingId === content.id ? (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                  <ContentForm
                    initial={content}
                    onSave={(data) => handleUpdate(content.id, data)}
                    onCancel={() => setEditingId(null)}
                    isLoading={updateMutation.isPending}
                  />
                </div>
              ) : (
                <div className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 px-4 py-3 group hover:border-slate-300 transition-colors">
                  <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                    <HardDrive className="h-4 w-4 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      {content.name}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                      {content.projectName && (
                        <span className="text-xs text-slate-500">
                          {content.projectName}
                        </span>
                      )}
                      {content.sizeGB && (
                        <span className="text-xs text-slate-400">
                          {parseFloat(content.sizeGB).toFixed(1)} GB
                        </span>
                      )}
                    </div>
                    {content.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {content.tags.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() =>
                              setFilterTag(filterTag === t ? "" : t)
                            }
                            className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                              filterTag === t
                                ? "bg-primary-600 text-white"
                                : "bg-slate-100 text-slate-500 hover:bg-primary-50 hover:text-primary-600"
                            }`}
                          >
                            #{t}
                          </button>
                        ))}
                      </div>
                    )}
                    {content.notes && (
                      <p className="text-xs text-slate-400 mt-1 truncate">
                        {content.notes}
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button
                        type="button"
                        onClick={() => setEditingId(content.id)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(content.id)}
                        disabled={deletingId === content.id}
                        className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
